import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import './App.css';
import { CartProvider, CartContext } from './context/CartContext';
import { ToastProvider } from './components/ui/Toast';
import { fetchProductsPage } from './utils/catalogApi';
import Skeleton from './components/ui/Skeleton';
import EmptyState from './components/ui/EmptyState';
import Button from './components/ui/Button';
import ProductCard from './components/ProductCard';
import Cart from './components/Cart';
import OrderForm from './components/OrderForm';
import OrderTracking from './components/OrderTracking';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';

function TrackingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const handleNewOrder = () => {
    navigate('/');
  };

  return (
    <OrderTracking
      token={token}
      onNewOrder={handleNewOrder}
    />
  );
}

function CheckoutPage() {
  const navigate = useNavigate();

  const handleOrderComplete = (orderId, token) => {
    navigate(`/tracking?token=${token}`);
  };

  return (
    <OrderForm
      onBack={() => navigate('/')}
      onOrderComplete={handleOrderComplete}
    />
  );
}

// Tags conhecidas acumulam entre páginas para o filtro não encolher ao rolar
const mergeTags = (current, incoming) => {
  const map = new Map(current.map(tag => [tag.id, tag]));
  incoming.forEach(tag => { if (!map.has(tag.id)) map.set(tag.id, tag); });
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const mergeProducts = (current, incoming) => {
  const ids = new Set(current.map(product => product.id));
  return [...current, ...incoming.filter(product => !ids.has(product.id))];
};

function HomePage() {
  const { cart, setIsCartOpen } = useContext(CartContext);
  const [catalog, setCatalog] = useState([]);
  const [catalogStatus, setCatalogStatus] = useState('loading');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeTag, setActiveTag] = useState(null);
  const [knownTags, setKnownTags] = useState([]);
  const [nextPage, setNextPage] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const sentinelRef = useRef(null);
  const requestIdRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadPage = useCallback(async (page, { append }) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (append) {
      loadingMoreRef.current = true;
      setIsLoadingMore(true);
      setLoadMoreError(false);
    } else {
      loadingMoreRef.current = false;
      setIsLoadingMore(false);
      setCatalogStatus('loading');
    }

    try {
      const data = await fetchProductsPage({ page, search: debouncedSearch, tag: activeTag });
      if (requestId !== requestIdRef.current) return;

      setCatalog(previous => (append ? mergeProducts(previous, data.products) : data.products));
      setKnownTags(previous => mergeTags(previous, data.products.flatMap(product => product.tags)));
      setTotalCount(data.count);
      setNextPage(data.hasMore ? data.nextPage : null);
      setCatalogStatus('ready');
    } catch {
      if (requestId !== requestIdRef.current) return;
      if (append) {
        setLoadMoreError(true);
      } else {
        setCatalogStatus('error');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        loadingMoreRef.current = false;
        setIsLoadingMore(false);
      }
    }
  }, [debouncedSearch, activeTag]);

  useEffect(() => {
    loadPage(1, { append: false });
  }, [loadPage]);

  // Carrega a próxima página quando o fim da lista entra na viewport
  useEffect(() => {
    if (catalogStatus !== 'ready' || !nextPage || loadMoreError) return undefined;

    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some(entry => entry.isIntersecting) && !loadingMoreRef.current) {
        loadPage(nextPage, { append: true });
      }
    }, { rootMargin: '400px 0px' });

    observer.observe(node);
    return () => observer.disconnect();
  }, [catalogStatus, nextPage, loadMoreError, loadPage]);

  const handleCheckout = () => {
    navigate('/checkout');
  };

  const customizableProducts = catalog.filter((product) => product.customizable);
  const nonCustomizableProducts = catalog.filter((product) => !product.customizable);

  const scrollToCatalog = () => {
    document.getElementById('produtos')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="storefront">
      <header className="site-header"><div className="header-inner">
        <a className="brand" href="/" aria-label="Arttibus - inicio"><span><strong>Arttibus</strong><small>Bottons Personalizados</small></span></a>
        <button onClick={() => setIsCartOpen(true)} className="cart-trigger" aria-label={`Abrir carrinho, ${cart.length} itens`}><svg className="cart-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M3 4h2l2.1 11.2a2 2 0 0 0 2 1.8h7.8a2 2 0 0 0 2-1.6L20.5 8H6M10 20a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm9 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" /></svg><span className="cart-label">Carrinho</span><span className="cart-count">{cart.length}</span></button>
      </div><div className="search-wrap"><svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" strokeWidth="2" /><path strokeLinecap="round" strokeWidth="2" d="m20 20-4-4" /></svg><input aria-label="Buscar produto" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Busque um produto" /></div></header>

      <section className="hero">
        <p className="hero-kicker">Feito à mão, do seu jeito</p>
        <h1>Bottons Personalizados</h1>
        <p>Envie sua imagem, escolha o formato e receba peças personalizadas produzidas aqui em Anápolis.</p>
        <div className="hero-socials">
          <a
            className="hero-social"
            href="https://www.instagram.com/arttibus/"
            target="_blank"
            rel="noreferrer"
            aria-label="Instagram da Arttibus"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="5" />
              <circle cx="12" cy="12" r="4" />
              <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
            </svg>
            Instagram
          </a>
          <a
            className="hero-social"
            href="https://wa.me/message/3Y3HIKZCYB7OM1"
            target="_blank"
            rel="noreferrer"
            aria-label="WhatsApp da Arttibus"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.05-.52-.099-.148-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.887-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.884 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
            WhatsApp
          </a>
        </div>
        <button className="hero-cta" onClick={scrollToCatalog}>Ver produtos</button>
      </section>

      <main className="catalog-main" id="produtos">
        {/* Filtro por tags (aplicado no backend, junto com a busca) */}
        {knownTags.length > 0 && (
          <div className="flex gap-2 flex-wrap py-2 mb-6 justify-center overflow-x-auto scrollbar-hide" role="group" aria-label="Filtrar por tema">
            <button
              type="button"
              className={`chip${!activeTag ? ' is-active' : ''}`}
              onClick={() => setActiveTag(null)}
            >
              Todos
            </button>
            {knownTags.map(tag => (
              <button
                key={tag.id}
                type="button"
                className={`chip${activeTag === tag.slug ? ' is-active' : ''}`}
                onClick={() => setActiveTag(activeTag === tag.slug ? null : tag.slug)}
              >
                {tag.name}
              </button>
            ))}
          </div>
        )}
        {catalogStatus === 'loading' && (
          <div className="product-grid" aria-busy="true" aria-label="Carregando catálogo">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="product-card" aria-hidden="true">
                <Skeleton className="h-[180px] w-full rounded-xl sm:h-[215px]" />
                <Skeleton className="mt-4 h-4 w-2/3" />
                <Skeleton className="mt-2 h-4 w-full" />
                <Skeleton className="mt-4 h-10 w-full rounded-full" />
              </div>
            ))}
          </div>
        )}
        {catalogStatus === 'error' && (
          <EmptyState
            title="Não foi possível carregar o catálogo"
            description="Verifique sua conexão e tente novamente."
            action={<Button onClick={() => loadPage(1, { append: false })}>Tentar novamente</Button>}
          />
        )}
        {catalogStatus === 'ready' && (
          <>
            {customizableProducts.length > 0 && (
              <section className="catalog-section">
                <div className="catalog-heading">
                  <h2>Nossos Produtos Personalizáveis</h2>
                  <p>Escolha seu produto favorito e personalize com sua imagem!</p>
                </div>
                <div className="product-grid">
                  {customizableProducts.map((product) => <ProductCard key={product.id} product={product} />)}
                </div>
              </section>
            )}
            {nonCustomizableProducts.length > 0 && (
              <section className="catalog-section">
                <div className="catalog-heading">
                  <h2>Produtos Prontos</h2>
                  <p>Itens já finalizados, prontos para comprar.</p>
                </div>
                <div className="product-grid">
                  {nonCustomizableProducts.map((product) => <ProductCard key={product.id} product={product} />)}
                </div>
              </section>
            )}
            {catalog.length === 0 && (
              <p className="empty-search">Nenhum produto encontrado. Tente outra busca.</p>
            )}

            {catalog.length > 0 && (
              <div className="catalog-pagination" ref={sentinelRef}>
                {isLoadingMore && (
                  <span className="catalog-pagination-status" role="status">
                    <span className="catalog-spinner" aria-hidden="true" />
                    Carregando mais produtos...
                  </span>
                )}
                {!isLoadingMore && loadMoreError && (
                  <Button variant="secondary" onClick={() => loadPage(nextPage, { append: true })}>
                    Não foi possível carregar mais. Tentar novamente
                  </Button>
                )}
                {!isLoadingMore && !loadMoreError && nextPage && (
                  <Button variant="secondary" onClick={() => loadPage(nextPage, { append: true })}>
                    Carregar mais produtos
                  </Button>
                )}
                {!isLoadingMore && !loadMoreError && !nextPage && (
                  <p className="catalog-end">Você viu todos os {totalCount} produtos.</p>
                )}
              </div>
            )}
          </>
        )}
      </main>

      <Cart onCheckout={handleCheckout} />

      <footer className="site-footer"><div><span className="footer-brand">Arttibus</span><p>Bottons Personalizados</p></div><div className="footer-links"><a href="tel:+556292096098">+55 62 9209-6098</a><a href="https://www.instagram.com/arttibus/" target="_blank" rel="noreferrer">@arttibus no Instagram ↗</a></div><small>© 2025 Arttibus</small></footer>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const [authState, setAuthState] = useState('loading'); // 'loading' | 'authenticated' | 'unauthenticated'

  useEffect(() => {
    // Usa um endpoint protegido real para verificar se a sessão Django é válida.
    // GET /api/orders/ retorna 200 com sessão ativa e 401/403 sem sessão.
    fetch(`${process.env.REACT_APP_API_URL}/api/orders/?page=1`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(res => {
        if (res.ok) {
          setAuthState('authenticated');
        } else {
          setAuthState('unauthenticated');
        }
      })
      .catch(() => setAuthState('unauthenticated'));
  }, []);

  if (authState === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream-100">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cocoa-800"></div>
      </div>
    );
  }

  if (authState === 'unauthenticated') {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
}

function AppContent() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/tracking" element={<TrackingPage />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <CartProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </CartProvider>
  );
}

export default App;
